import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { ICountingAlgorithm, TallySheetVotingChannel, VotingStatusChannel } from "@sequentech/ui-core";
import { useTranslation } from "react-i18next";
import { ResultsAndParticipation, } from "@sequentech/ui-essentials";
import { translatedLabel } from "@/services/resultLabels";
import { entityClassName } from "@/services/cssClassNames";
import { orderResultCandidates } from "@/services/resultsOrdering";
var getNumber = function (value) {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string") {
        var parsed = Number(value.trim());
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};
var getId = function (value) {
    return typeof value === "string" || typeof value === "number" ? value : undefined;
};
var sameId = function (left, right) {
    return left !== null &&
        left !== undefined &&
        right !== null &&
        right !== undefined &&
        String(left) === String(right);
};
var isRecord = function (value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};
var parseAnnotations = function (annotations) {
    var parsed = annotations;
    if (typeof annotations === "string") {
        try {
            parsed = JSON.parse(annotations);
        }
        catch (_a) {
            return null;
        }
    }
    return isRecord(parsed) ? parsed : null;
};
export var ContestResultsBlock = function (_a) {
    var _b;
    var manifestContest = _a.manifestContest, dataset = _a.dataset, locale = _a.locale;
    var t = useTranslation().t;
    var contest = dataset.contest.find(function (row) { return sameId(row.id, manifestContest.contest_id); });
    var election = dataset.election.find(function (row) { return sameId(row.id, manifestContest.election_id); });
    var resultContest = useMemo(function () {
        var source = manifestContest.area_id
            ? dataset.results_area_contest
            : dataset.results_contest;
        return source.find(function (row) {
            return sameId(row.contest_id, manifestContest.contest_id) &&
                sameId(row.election_id, manifestContest.election_id) &&
                (!manifestContest.area_id || sameId(row.area_id, manifestContest.area_id));
        });
    }, [dataset, manifestContest]);
    var resultsCandidates = manifestContest.area_id
        ? dataset.results_area_contest_candidate
        : dataset.results_contest_candidate;
    var candidates = useMemo(function () {
        return orderResultCandidates(dataset, manifestContest.contest_id, locale).map(function (candidate) {
            var result = resultsCandidates.find(function (row) {
                return sameId(row.candidate_id, candidate.id) &&
                    sameId(row.contest_id, manifestContest.contest_id) &&
                    sameId(row.election_id, manifestContest.election_id) &&
                    (!manifestContest.area_id || sameId(row.area_id, manifestContest.area_id));
            });
            return {
                id: String(candidate.id),
                name: translatedLabel(candidate, locale),
                castVotes: getNumber(result === null || result === void 0 ? void 0 : result.cast_votes),
                castVotesPercent: getNumber(result === null || result === void 0 ? void 0 : result.cast_votes_percent),
                winningPosition: getNumber(result === null || result === void 0 ? void 0 : result.winning_position),
            };
        });
    }, [dataset, resultsCandidates, manifestContest, locale]);
    var summary = useMemo(function () {
        var _a, _b, _c, _d, _e, _f;
        if (!resultContest)
            return null;
        return {
            id: getId(resultContest.id),
            eligibleCensus: getNumber(resultContest.elegible_census),
            totalAuditableVotes: getNumber(resultContest.total_auditable_votes),
            totalAuditableVotesPercent: getNumber(resultContest.total_auditable_votes_percent),
            totalVotes: getNumber(resultContest.total_votes),
            totalVotesPercent: getNumber(resultContest.total_votes_percent),
            totalValidVotes: getNumber(resultContest.total_valid_votes),
            totalValidVotesPercent: getNumber(resultContest.total_valid_votes_percent),
            totalInvalidVotes: getNumber(resultContest.total_invalid_votes),
            totalInvalidVotesPercent: getNumber(resultContest.total_invalid_votes_percent),
            explicitInvalidVotes: getNumber(resultContest.explicit_invalid_votes),
            explicitInvalidVotesPercent: getNumber(resultContest.explicit_invalid_votes_percent),
            implicitInvalidVotes: getNumber(resultContest.implicit_invalid_votes),
            implicitInvalidVotesPercent: getNumber(resultContest.implicit_invalid_votes_percent),
            blankVotes: getNumber((_a = resultContest.total_blank_votes) !== null && _a !== void 0 ? _a : resultContest.blank_votes),
            blankVotesPercent: getNumber((_b = resultContest.total_blank_votes_percent) !== null && _b !== void 0 ? _b : resultContest.blank_votes_percent),
            explicitBlankVotes: getNumber(resultContest.explicit_blank_votes),
            explicitBlankVotesPercent: getNumber(resultContest.explicit_blank_votes_percent),
            implicitBlankVotes: getNumber(resultContest.implicit_blank_votes),
            implicitBlankVotesPercent: getNumber(resultContest.implicit_blank_votes_percent),
            weight: getNumber((_d = (_c = parseAnnotations(resultContest.annotations)) === null || _c === void 0 ? void 0 : _c.extended_metrics) === null || _d === void 0 ? void 0 : _d.weight),
            votesByChannel: (_f = (_e = parseAnnotations(resultContest.annotations)) === null || _e === void 0 ? void 0 : _e.extended_metrics) === null || _f === void 0 ? void 0 : _f.votes_by_channel,
        };
    }, [resultContest]);
    var processResults = useMemo(function () {
        var _a, _b;
        if ((contest === null || contest === void 0 ? void 0 : contest.counting_algorithm) !== ICountingAlgorithm.INSTANT_RUNOFF) {
            return null;
        }
        return (_b = (_a = parseAnnotations(resultContest === null || resultContest === void 0 ? void 0 : resultContest.annotations)) === null || _a === void 0 ? void 0 : _a.process_results) !== null && _b !== void 0 ? _b : null;
    }, [contest === null || contest === void 0 ? void 0 : contest.counting_algorithm, resultContest === null || resultContest === void 0 ? void 0 : resultContest.annotations]);
    var preferential = (contest === null || contest === void 0 ? void 0 : contest.counting_algorithm) === ICountingAlgorithm.INSTANT_RUNOFF;
    // An acclaimed contest was decided without a vote: every candidate wins
    // with zero votes, so participation figures are replaced by a note.
    var isAcclaimed = Boolean(contest === null || contest === void 0 ? void 0 : contest.is_acclaimed);
    var title = (_b = (typeof (resultContest === null || resultContest === void 0 ? void 0 : resultContest.name) === "string" && resultContest.name.length > 0
        ? resultContest.name
        : undefined)) !== null && _b !== void 0 ? _b : translatedLabel(contest, locale, t("resultsPortal.fallbackContestName", { contestId: manifestContest.contest_id }));
    var electionName = translatedLabel(election, locale, t("resultsPortal.fallbackElectionName"));
    var area = dataset.area.find(function (row) { return sameId(row.id, manifestContest.area_id); });
    var areaName = manifestContest.area_id
        ? translatedLabel(area, locale, manifestContest.area_id)
        : null;
    var chartName = areaName
        ? "".concat(electionName, " - ").concat(title, " - ").concat(areaName)
        : "".concat(electionName, " - ").concat(title);
    var isPublished = manifestContest.publication_state === "published";
    var labels = useMemo(function () {
        var _a;
        return ({
            participationSummary: t("resultsPortal.resultsAndParticipation.participationSummary"),
            candidateResults: t("resultsPortal.resultsAndParticipation.candidateResults"),
            total: t("resultsPortal.resultsAndParticipation.total"),
            turnout: t("resultsPortal.resultsAndParticipation.turnout"),
            eligibleCensus: t("resultsPortal.resultsAndParticipation.eligibleCensus"),
            totalAuditableVotes: t("resultsPortal.resultsAndParticipation.totalAuditableVotes"),
            totalVotesCounted: t("resultsPortal.resultsAndParticipation.totalVotesCounted"),
            totalValidVotes: t("resultsPortal.resultsAndParticipation.totalValidVotes"),
            totalInvalidVotes: t("resultsPortal.resultsAndParticipation.totalInvalidVotes"),
            explicitInvalidVotes: t("resultsPortal.resultsAndParticipation.explicitInvalidVotes"),
            implicitInvalidVotes: t("resultsPortal.resultsAndParticipation.implicitInvalidVotes"),
            blankVotes: t("resultsPortal.resultsAndParticipation.blankVotes"),
            explicitBlankVotes: t("resultsPortal.resultsAndParticipation.explicitBlankVotes"),
            implicitBlankVotes: t("resultsPortal.resultsAndParticipation.implicitBlankVotes"),
            blankVotesChart: t("resultsPortal.resultsAndParticipation.blankVotesChart"),
            weight: t("resultsPortal.resultsAndParticipation.weight"),
            options: t("resultsPortal.resultsAndParticipation.options"),
            castVotes: t("resultsPortal.resultsAndParticipation.castVotes"),
            castVotesPercent: t("resultsPortal.resultsAndParticipation.castVotesPercent"),
            winningPosition: t("resultsPortal.resultsAndParticipation.winningPosition"),
            votesForCandidates: t("resultsPortal.resultsAndParticipation.votesForCandidates"),
            invalidVotes: t("resultsPortal.resultsAndParticipation.invalidVotes"),
            nonVoters: t("resultsPortal.resultsAndParticipation.nonVoters"),
            others: t("resultsPortal.resultsAndParticipation.others"),
            candidate: t("resultsPortal.resultsAndParticipation.candidate"),
            round: t("resultsPortal.resultsAndParticipation.round"),
            winner: t("resultsPortal.resultsAndParticipation.winner"),
            eliminated: t("resultsPortal.resultsAndParticipation.eliminated"),
            empty: t("resultsPortal.resultsAndParticipation.empty"),
            previousRounds: t("resultsPortal.resultsAndParticipation.previousRounds"),
            nextRounds: t("resultsPortal.resultsAndParticipation.nextRounds"),
            participationByChannel: t("resultsPortal.resultsAndParticipation.participationByChannel"),
            channel: t("resultsPortal.resultsAndParticipation.channel"),
            acclamationNote: t("resultsPortal.acclamationNote"),
            channelNames: (_a = {},
                _a[VotingStatusChannel.Online] = t("resultsPortal.resultsAndParticipation.channelOnline"),
                _a[VotingStatusChannel.Kiosk] = t("resultsPortal.resultsAndParticipation.channelKiosk"),
                _a[VotingStatusChannel.EarlyVoting] = t("resultsPortal.resultsAndParticipation.channelEarlyVoting"),
                _a[VotingStatusChannel.Telephone] = t("resultsPortal.resultsAndParticipation.channelTelephone"),
                _a[TallySheetVotingChannel.Paper] = t("resultsPortal.resultsAndParticipation.channelPaper"),
                _a[TallySheetVotingChannel.Postal] = t("resultsPortal.resultsAndParticipation.channelPostal"),
                _a[TallySheetVotingChannel.InPerson] = t("resultsPortal.resultsAndParticipation.channelInPerson"),
                _a),
        });
    }, [t]);
    return (_jsxs(Box, { className: [
            "seq-results-contest",
            entityClassName("election", manifestContest.election_id),
            entityClassName("contest", manifestContest.contest_id),
            entityClassName("area", manifestContest.area_id),
            "seq-results-contest--".concat(manifestContest.publication_state),
            preferential
                ? "seq-results-contest--preferential"
                : "seq-results-contest--candidate",
            isAcclaimed ? "seq-results-contest--acclaimed" : "",
        ]
            .filter(Boolean)
            .join(" "), component: "section", sx: {
            mt: 3,
            p: { xs: 2, sm: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
        }, children: [_jsxs(Stack, { className: "seq-results-contest__header", direction: { xs: "column", sm: "row" }, spacing: 1.5, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, children: [_jsxs(Box, { className: "seq-results-contest__intro", children: [_jsx(Typography, { className: "seq-results-contest__title", component: "h3", variant: "h6", children: title }), _jsxs(Typography, { className: "seq-results-contest__meta", variant: "body2", color: "text.secondary", children: [electionName, manifestContest.positions
                                        ? " - ".concat(t(manifestContest.positions === 1
                                            ? "resultsPortal.position"
                                            : "resultsPortal.position_plural", { count: manifestContest.positions }))
                                        : "", areaName ? " - ".concat(areaName) : ""] })] }), isAcclaimed ? (_jsx(Chip, { className: "seq-results-contest__acclaimed-chip", label: t("resultsPortal.acclaimed"), color: "info", variant: "outlined", size: "small" })) : null, _jsx(Chip, { className: "seq-results-contest__status-chip", label: isPublished
                            ? t("resultsPortal.published")
                            : t("resultsPortal.notPublishedYet"), color: isPublished ? "success" : "default", variant: isPublished ? "filled" : "outlined", size: "small" })] }), !isPublished ? (_jsx(Typography, { className: "seq-results-contest__not-published", sx: { mt: 3 }, color: "text.secondary", children: t("resultsPortal.notPublishedYet") })) : (_jsx(Box, { className: "seq-results-contest__results", children: _jsx(ResultsAndParticipation, { summary: summary, candidates: candidates, chartName: chartName, processResults: processResults, preferential: preferential, labels: labels, acclaimed: isAcclaimed }) }))] }));
};
